import React from "react";
import styles from './styles.module.scss';
import { useFormikContext } from "formik";
import { Checkbox } from "components/Checkbox";
import { UserInfo } from "context/user";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import { ErrorType } from "actions/lib/baseActions";
import { Dropdown } from "components/Forms/Dropdown";
import { SearchInput } from "components/SearchInput";
import { useSortedUsers } from "pages/UserRoles/UsersTable/useSortedUsers";
import { AttentionMessage } from "components/AttentionMessage";
import { RoleType } from "actions/roleActions";

interface AssignDepartmentUsersTableProps {
  setAssignUsersError: (error: ErrorType | null) => void;
}

export const AssignDepartmentUsersTable = React.memo(({ 
  setAssignUsersError 
}: AssignDepartmentUsersTableProps) => {

  const { values, setFieldValue, setValues, setSubmitting } = useFormikContext<any>();

  const setUsers = (users: (UserInfo & RoleType)[] | undefined) => {
    setFieldValue("users", users);
  }

  const {
    userSortDropdownOptions,
    onSortOptionChange,
    populateUsers,
    sortedUsers
  } = useSortedUsers(values.users, setUsers);

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => { 
    const userId = parseInt(e.target.id);
    let assignedDepartmentUsersIds: number[] = values.assignedUserIds || [];
    if (e.target.checked) {
      assignedDepartmentUsersIds.push(userId);
    } else {
      assignedDepartmentUsersIds = assignedDepartmentUsersIds.filter(id => (id !== userId));
    }
    setValues({ ...values, assignedUserIds: Array.from(new Set(assignedDepartmentUsersIds)) });
    setAssignUsersError(null);
    setSubmitting(false);
  }

  const assignedDepartmentUsersCount = values?.assignedUserIds?.length;
  const pluralOrSingularUserId = (assignedDepartmentUsersCount > 1) ? "users" : "user";

  return (
    <div className={styles.assignDepartmentUsersTableContainer}>
      <div className={styles.assignDepartmentUsersTableHeaders}>
        <p className={classNames((assignedDepartmentUsersCount <= 0) && styles.assignedNone, styles.smallText)}>
          <FormattedMessage id="assignedTo" defaultMessage="Assigned to"/> {assignedDepartmentUsersCount} <FormattedMessage id={pluralOrSingularUserId} defaultMessage={pluralOrSingularUserId}/>
        </p>
        <div className={styles.departmentsSortSearch}>
          <Dropdown dropdownId="assignDepartmentUserSortOptions" key="assignDepartmentUserSortOptions"
            dropdownOptions={userSortDropdownOptions}
            onSelect={onSortOptionChange}/>
          <SearchInput className={styles.searchInput} onChange={populateUsers}/>
        </div> 
      </div>

      {!sortedUsers || sortedUsers?.length === 0 && 
        <div className={styles.noUsersMessage}>
          <AttentionMessage message="message.noUsers"></AttentionMessage>
        </div>
      }

      <div className={styles.assignDepartmentUsersTable}>
        {sortedUsers?.map((user: UserInfo & RoleType) => {
          const userFullName= user.fullName;
          const userId = user.id;
          const isAssigned = values.assignedUserIds?.includes(userId);
          const roleName = user.roleName;

          return (
            <div className={styles.userRow} key={userId}>
              <div className={styles.userNameAndRole}>
                <h4>{userFullName}</h4>
                {roleName && (
                  <p className={classNames(styles.roleName, styles.smallText)}>
                    <FormattedMessage id={roleName} defaultMessage={roleName}/>
                  </p>
                )}
              </div>
              <Checkbox 
                name={userFullName}
                id={userId}
                value={isAssigned}
                onChange={onCheckboxChange}
              />
            </div>
          )
        })}
      </div>
    </div>
  );
});
